import { styled } from '@mui/material';

type Props = {
  label: string;
  /** Number of items behind the tab. Hidden when zero or undefined. */
  count?: number;
  /** Accessible wording for the count, e.g. `${count} e-mails à traiter`. */
  countLabel?: string;
};

const COUNT_TINT_OPACITY = 0.1;
const MAX_DISPLAYED_COUNT = 99;

const Root = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Count = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  minWidth: theme.spacing(3),
  paddingInline: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.alpha(theme.palette.primary.main, COUNT_TINT_OPACITY),
  color: theme.palette.primary.main,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
}));

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
});

/** Tab label with an optional item count pill, used inside `Tab`. */
export function TabLabel(props: Props) {
  const count = props.count ?? 0;
  const hasCount = count > 0;
  const displayedCount = count > MAX_DISPLAYED_COUNT ? `${MAX_DISPLAYED_COUNT}+` : count;

  return (
    <Root>
      {props.label}
      {hasCount ? (
        <>
          <Count aria-hidden={props.countLabel ? 'true' : undefined}>{displayedCount}</Count>
          {props.countLabel ? <VisuallyHidden>{`(${props.countLabel})`}</VisuallyHidden> : null}
        </>
      ) : null}
    </Root>
  );
}

export type { Props as TabLabelProps };
